import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Play, Pause, Clock, Mail, MessageSquare, MessageCircle, Users, X } from "lucide-react";
import api from "@/lib/api";

const TRIGGER_LABELS = {
  registration: "New Registration", course_purchase: "Course Purchase",
  payment_success: "Premium/Certification Purchase", certificate_issued: "Certificate Issued",
  inactive_user: "Inactive User",
};

const STEP_ICON = { wait: Clock, send_email: Mail, send_sms: MessageSquare, send_whatsapp: MessageCircle };

const EMPTY_STEP = { type: "send_email", days: 1, template_id: "" };

export default function AutomationsPanel({ twilioConfigured }) {
  const [automations, setAutomations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [triggerType, setTriggerType] = useState("registration");
  const [inactiveDays, setInactiveDays] = useState(30);
  const [steps, setSteps] = useState([{ ...EMPTY_STEP }]);
  const [creating, setCreating] = useState(false);
  const [viewingRuns, setViewingRuns] = useState(null);
  const [runs, setRuns] = useState([]);

  const load = () => {
    api.get("/admin/crm/automations").then((r) => setAutomations(r.data)).catch(() => toast.error("Could not load automations"));
  };

  useEffect(() => {
    load();
    api.get("/admin/crm/templates").then((r) => setTemplates(r.data));
  }, []);

  const addStep = () => setSteps([...steps, { ...EMPTY_STEP }]);
  const removeStep = (i) => setSteps(steps.filter((_, idx) => idx !== i));
  const updateStep = (i, patch) => setSteps(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const createAutomation = async () => {
    if (!name.trim()) { toast.error("Automation name is required"); return; }
    for (const s of steps) {
      if (s.type.startsWith("send_") && !s.template_id) { toast.error("Every send step needs a template"); return; }
    }
    setCreating(true);
    try {
      await api.post("/admin/crm/automations", {
        name, trigger_type: triggerType, inactive_days: inactiveDays,
        steps: steps.map((s) => ({ type: s.type, days: s.type === "wait" ? parseInt(s.days) : null, template_id: s.type.startsWith("send_") ? s.template_id : null })),
      });
      toast.success("Automation created");
      setName(""); setSteps([{ ...EMPTY_STEP }]); setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create automation");
    } finally {
      setCreating(false);
    }
  };

  const toggleAutomation = async (id) => {
    const { data } = await api.post(`/admin/crm/automations/${id}/toggle`);
    setAutomations((prev) => prev.map((a) => (a.id === id ? { ...a, status: data.status } : a)));
  };

  const deleteAutomation = async (id) => {
    await api.delete(`/admin/crm/automations/${id}`);
    setAutomations((prev) => prev.filter((a) => a.id !== id));
    toast.success("Automation deleted");
  };

  const viewRuns = async (id) => {
    setViewingRuns(id);
    const { data } = await api.get(`/admin/crm/automations/${id}/runs`);
    setRuns(data.runs);
  };

  return (
    <div data-testid="crm-automations-panel">
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowForm((s) => !s)} data-testid="crm-add-automation-toggle-btn"
          className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-white px-3 py-2 rounded-lg">
          <Plus className="w-3.5 h-3.5" /> New Automation
        </button>
      </div>

      {showForm && (
        <div className="glass-panel rounded-xl p-4 mb-6" data-testid="crm-automation-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Automation Name (e.g. Welcome Journey)"
              data-testid="crm-automation-name-input" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white" />
            <select value={triggerType} onChange={(e) => setTriggerType(e.target.value)} data-testid="crm-automation-trigger-select"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white">
              {Object.entries(TRIGGER_LABELS).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
            </select>
          </div>
          {triggerType === "inactive_user" && (
            <input type="number" value={inactiveDays} onChange={(e) => setInactiveDays(parseInt(e.target.value) || 30)} placeholder="Inactive days"
              data-testid="crm-automation-inactive-days-input" className="w-40 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white mb-3" />
          )}

          <p className="text-xs font-semibold text-slate-300 mb-2">Journey Steps (executed in order)</p>
          <div className="space-y-2 mb-3">
            {steps.map((s, i) => {
              const Icon = STEP_ICON[s.type];
              return (
                <div key={i} className="flex items-center gap-2 bg-white/5 rounded-lg p-2" data-testid={`crm-automation-step-${i}`}>
                  <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <select value={s.type} onChange={(e) => updateStep(i, { type: e.target.value })} data-testid={`crm-automation-step-type-${i}`}
                    className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white">
                    <option value="wait">Wait</option>
                    <option value="send_email">Send Email</option>
                    <option value="send_sms" disabled={!twilioConfigured}>Send SMS {!twilioConfigured && "(coming soon)"}</option>
                    <option value="send_whatsapp" disabled={!twilioConfigured}>Send WhatsApp {!twilioConfigured && "(coming soon)"}</option>
                  </select>
                  {s.type === "wait" ? (
                    <input type="number" min="1" value={s.days} onChange={(e) => updateStep(i, { days: e.target.value })} data-testid={`crm-automation-step-days-${i}`}
                      className="w-20 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white" placeholder="days" />
                  ) : (
                    <select value={s.template_id} onChange={(e) => updateStep(i, { template_id: e.target.value })} data-testid={`crm-automation-step-template-${i}`}
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white">
                      <option value="">Select template…</option>
                      {templates.filter((t) => t.channel === s.type.replace("send_", "")).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  )}
                  <button onClick={() => removeStep(i)} data-testid={`crm-remove-step-btn-${i}`}><X className="w-3.5 h-3.5 text-red-400 hover:text-red-300" /></button>
                </div>
              );
            })}
          </div>
          <button onClick={addStep} data-testid="crm-add-step-btn" className="text-xs text-indigo-300 hover:text-indigo-200 mb-4">+ Add another step</button>
          <br />
          <button onClick={createAutomation} disabled={creating} data-testid="crm-save-automation-btn"
            className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
            {creating ? "Creating…" : "Create Automation"}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {automations.map((a) => (
          <div key={a.id} className="glass-panel rounded-xl p-4" data-testid={`crm-automation-card-${a.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-white">{a.name}</p>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${a.status === "active" ? "bg-emerald-400/10 text-emerald-300" : "bg-slate-500/10 text-slate-400"}`}>{a.status}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">Trigger: {TRIGGER_LABELS[a.trigger_type]} · {a.steps.length} step(s) · {a.stats.enrolled} enrolled · {a.stats.completed} completed</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => viewRuns(a.id)} data-testid={`crm-view-runs-btn-${a.id}`} className="flex items-center gap-1 text-xs text-slate-300 hover:text-white"><Users className="w-3.5 h-3.5" /> Enrolled</button>
                <button onClick={() => toggleAutomation(a.id)} data-testid={`crm-toggle-automation-btn-${a.id}`} className="flex items-center gap-1 text-xs text-slate-300 hover:text-white">
                  {a.status === "active" ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />} {a.status === "active" ? "Pause" : "Activate"}
                </button>
                <button onClick={() => deleteAutomation(a.id)} data-testid={`crm-delete-automation-btn-${a.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
        {automations.length === 0 && <p className="text-sm text-slate-400 text-center py-8" data-testid="crm-no-automations">No automations yet — create a journey that triggers on signup, purchase, or inactivity.</p>}
      </div>

      {viewingRuns && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setViewingRuns(null)} data-testid="crm-automation-runs-modal">
          <div className="glass-panel rounded-2xl p-5 max-w-lg w-full max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-white">Enrolled Contacts</p>
              <button onClick={() => setViewingRuns(null)}><X className="w-4 h-4 text-slate-400 hover:text-white" /></button>
            </div>
            {runs.length === 0 && <p className="text-xs text-slate-500">No one enrolled yet.</p>}
            {runs.map((r) => (
              <div key={r.id} className="flex items-center justify-between border-b border-white/5 py-2 text-xs" data-testid={`crm-run-row-${r.id}`}>
                <span className="text-slate-300">{r.contact?.name} ({r.contact?.email})</span>
                <span className="text-slate-500">step {r.current_step} · {r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
